import { Close, Description, Provider, Root, Title, Viewport } from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import { type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ToastTone } from '@/shared/utils/toast-store/toast-store.types';
import { cn } from '@/shared/utils/cn/cn.util';

import { toastVariants } from './Toaster.tokens';

const ICON_BY_TONE: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 aria-hidden className="h-5 w-5 text-success" />,
  error: <XCircle aria-hidden className="h-5 w-5 text-destructive" />,
  warning: <AlertTriangle aria-hidden className="h-5 w-5 text-warning" />,
  info: <Info aria-hidden className="h-5 w-5 text-primary" />,
  loading: <Loader2 aria-hidden className="h-5 w-5 animate-spin text-muted-foreground" />,
};

const TOAST_STYLE: CSSProperties = { pointerEvents: 'auto' };

/**
 * Mounted ONCE, in the shell.
 *
 * Radix owns the swipe-to-dismiss, the hover-pause and — the part worth having
 * — the live region, so a toast is announced without stealing focus from
 * whatever the operator was typing. An error is announced assertively; anything
 * else politely.
 *
 * The viewport is pinned to the BOTTOM INLINE-END corner, which is bottom-left
 * in Arabic and bottom-right in English, with no conditional anywhere.
 */
export function Toaster() {
  const { toasts, dismiss } = useToast();
  const { t } = useTranslation('uiKit');

  return (
    <Provider swipeDirection="right">
      {toasts.map((toast) => (
        <Root
          key={toast.id}
          // Every toast is controlled by the store; Radix's own timer is off
          // (`duration: Infinity`) so there is exactly one clock, in one place.
          open
          duration={Infinity}
          type={toast.tone === 'error' ? 'foreground' : 'background'}
          onOpenChange={(open) => {
            if (!open) dismiss(toast.id);
          }}
          className={toastVariants({ tone: toast.tone })}
          // Inline, not a class: a toast is a DismissableLayer, and once a
          // modal opens ABOVE a pinned one Radix writes `pointer-events: none`
          // inline on it — clicks then fall through to the sheet behind.
          style={TOAST_STYLE}
        >
          <span className="shrink-0">{ICON_BY_TONE[toast.tone]}</span>
          <div className="flex min-w-0 flex-1 flex-col gap-xs">
            <Title className="text-label text-foreground">{toast.title}</Title>
            {toast.description === undefined ? null : (
              <Description className="text-caption text-muted-foreground">
                {toast.description}
              </Description>
            )}
          </div>
          <Close
            aria-label={t('toast.dismiss')}
            className={cn(
              'shrink-0 rounded-sm p-xs text-muted-foreground transition-colors duration-fast ease-standard',
              'hover:bg-secondary hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
            )}
          >
            <X aria-hidden className="h-4 w-4" />
          </Close>
        </Root>
      ))}
      <Viewport
        // Radix's default is the hardcoded English "Notifications ({hotkey})".
        // It is the region label a screen-reader user hears on F8, so leaving it
        // untranslated puts one English string into an otherwise Arabic portal.
        label={t('toast.region')}
        // `w-full` below `sm`, a fixed column above it: the stack used to be a
        // flat `w-96` (384px), which is WIDER THAN A 320px PHONE — every toast
        // hung off the inline edge of the viewport, in both directions. The
        // gutter shrinks with it, since 24px each side of a 320px screen leaves
        // the message barely half the display.
        className="pointer-events-none fixed bottom-0 end-0 z-toast flex w-full flex-col gap-sm p-md pb-[calc(theme(spacing.md)+theme(spacing.safe-b))] sm:w-dialog-sm sm:p-xl"
      />
    </Provider>
  );
}
